'use client'

import { fetchAutocompleteResults } from '@/app/api/search'
import Button from '@/app/components/common/Button'
import Popup from '@/app/components/common/Popup'
import FormInput from '@/app/components/Input'
import SearchAutosuggest from '@/app/components/SearchAutosuggest'
import {
	AvailableInputTypes,
	COOKIE_NOTICE_STORAGE_KEY,
	DATE_FORMAT_QUERY,
	DEFAULT_ALLOCATION,
	DEFAULT_CURRENCY,
	defaultCheckInDate,
	defaultCheckOutDate,
	FORM_TYPE,
	INIT_DATA_STORAGE_KEY,
	INPUT,
	LG_BREAKPOINT,
	MD_BREAKPOINT,
	PAGE_TYPE,
	REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY,
	ROOM_GUESTS_SEPARATOR,
	SM_BREAKPOINT,
	SM_HEIGHT_BREAKPOINT,
} from '@/app/constants'
import {
	Allocation,
	SanityData,
	SearchItem,
	SearchItemLocationType,
	Suggestion,
	UserData,
} from '@/app/types'
import {
	capitalize,
	checkDate,
	getFormData,
	getGuestsCount,
	getDateLocaleByLangCode,
	getRedirectPartnerInfo,
	getComparisonCheckboxText,
	parseGooglePlaceToSelectedItem,
	parseSearchParamsToLower,
	scrollToSelectedMonth,
	serialize,
	smoothScrollTo,
	submitHiddenForm,
	validateDateRange,
	validateEmail
} from '@/app/utils'
import { buildRedirectUrl, isKayakMetaFlow } from '@/app/utils/kayakRedirect'
import {
	normalizeBackPartnerParams,
	normalizeFrontPartnerParams,
	normalizePartnerParams,
} from '@/app/utils/partner-mapping'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { addDays, addMonths, addWeeks, format, isValid } from 'date-fns'
import { camelCase, find, kebabCase, startCase } from 'lodash'
import { useTranslations } from 'next-intl'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { FC, FormEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
	SuggestionSelectedEventData,
	SuggestionsFetchRequestedParams,
} from 'react-autosuggest'

import { priceAlertSubscription } from '@/app/api/subscribe'
import BookingPlaceholder from '@/app/components/BookingPlaceholder'
import Checkbox from '@/app/components/common/Checkbox'
import EmailSignup from '@/app/components/EmailSignup'
import { DisplayData } from '@/app/components/LocationDataHandler'
import { Icons } from '@/app/components/ui/Icons'

// Dynamic imports for heavy components that are only needed on user interaction
const BookingDatepicker = dynamic(
	() => import('@/app/components/BookingDatepicker'),
	{ ssr: false }
)
const GuestsSelect = dynamic(
	() => import('@/app/components/GuestsSelect'),
	{ ssr: false }
)
const FullScreenDrawer = dynamic(
	() => import('@/app/components/common/FullScreenDrawer'),
	{ ssr: false }
)
import eventBus from '@/app/eventBus'
import {
	emitFormEmailSubmitGAEvent,
	emitFormEmailSubscribeBounceGAEvent,
	emitFormSearchGAEvent,
	emitFromDateGAEvent,
	emitGuestsClickGAEvent,
	emitGuestsSelectGAEvent,
	emitSearchWithoutLocationGAEvent,
	emitSignUpDealsGAEvent,
	emitToDateGAEvent,
} from '@/app/ga-event-helpers'
import { PS_EVENTS } from '@/app/ps-events'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import * as Sentry from '@sentry/nextjs'
import dynamic from 'next/dynamic'
import { RemoveScroll } from 'react-remove-scroll'
import useGoogleSignin from '../hooks/useGoogleSignin'
import { parseStayDateFromUrlOrLegacy } from '../utils/calendar-date'

export interface Props {
	displayValue?: DisplayData
	defaultLocation?: SearchItem | null
	isMainPage?: boolean
	isHotelLandingPage?: boolean
	sanityData?: SanityData
	loading?: boolean
	isHotelPage?: boolean
	customHeading?: boolean
	hotel?: any
	isExpanded?: boolean
	onClose?: () => void
	mobileHeadingSlot?: ReactNode
}

const Booking: FC<Props> = ({
	displayValue,
	defaultLocation,
	isMainPage,
	sanityData,
	isHotelLandingPage,
	loading,
	onClose,
	hotel,
	isHotelPage,
	isExpanded,
	customHeading = false,
	mobileHeadingSlot,
}) => {
	const router = useRouter()
	const searchParams = useSearchParams()
	const urlSearchParams = new URLSearchParams(searchParams.toString())
	const urlParams = useParams()
	const [_, setFrontFormData] = useLocalStorage(
		REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY
	)
	const [userData, setUserData] = useLocalStorage<UserData>(
		INIT_DATA_STORAGE_KEY
	)
	const [_cookie, setShowCookieNotice] = useLocalStorage(
		COOKIE_NOTICE_STORAGE_KEY
	)
	const newLogin = useFeatureIsOn("bhp-sign-up");
	const authData = useGoogleSignin(newLogin && isMainPage);

	const getValidDate = (paramName: string, defaultDate: Date) => {
		const dateParam = searchParams.get(paramName)
		if (dateParam && checkDate(dateParam)) {
			const d = parseStayDateFromUrlOrLegacy(dateParam)
			return isValid(d) ? d : null			
		}
		return null
	}

	const checkInDate = getValidDate('check-in-date', defaultCheckInDate)
	const checkOutDate = getValidDate('check-out-date', defaultCheckOutDate)

	const lowerCaseSearchParams = parseSearchParamsToLower(searchParams)
	const destCode = lowerCaseSearchParams?.destcode
	const preopenCalendar = lowerCaseSearchParams?.['cal']
	const shouldPreopenCalendar = preopenCalendar === '1'
	const roomsInputRef = useRef<HTMLDivElement>(null)
	const bookingRef = useRef<HTMLDivElement>(null)
	const redirectFormRef = useRef<HTMLFormElement>(null)
	const redirectFormBlankRef = useRef<HTMLFormElement>(null)
	const bookingInputsRef = useRef<HTMLDivElement>(null)
	const bookingInputDestRef = useRef<HTMLInputElement>(null)
	const bookingInputGuestsRef = useRef<HTMLInputElement>(null)
	const selectedItemRef = useRef<SearchItem | null>(null)
	const isDesktop = useMediaQuery(LG_BREAKPOINT)
	const isMd = useMediaQuery(MD_BREAKPOINT)
	const isMobile = useMediaQuery(SM_BREAKPOINT)
	const smHeight = useMediaQuery(SM_HEIGHT_BREAKPOINT)
	const [suggestionsResult, setSuggestionsResult] = useState<SearchItem[]>([])
	const [suggestions, setSuggestions] = useState<Suggestion[]>([])
	const [selectedSuggestion, setSelectedSuggestion] = useState<string>()
	const [selectedItem, setSelectedItem] = useState<SearchItem | null>(null)
	const [searchValue, setSearchValue] = useState<DisplayData>()
	const [openedInput, setOpenedInput] = useState<AvailableInputTypes>(null)
	const t = useTranslations('Form')
	const locale = getDateLocaleByLangCode(urlParams.lang as string)
	const [dateFrom, setDateFrom] = useState<Date | null>(checkInDate || null)
	const [dateTo, setDateTo] = useState<Date | null>(checkOutDate || null)
	const [roomsAndGuests, setRoomsAndGuests] = useState<Allocation[]>([
		DEFAULT_ALLOCATION,
	])
	const [roomsAndGuestsText, setRoomsAndGuestsText] = useState<string>(
		t('roomsAndGuests', { roomsCount: 1, guestsCount: 2 })
	)
	const [previousDateFrom, setPreviousDateFrom] = useState<Date | null>(null)
	const [previousDateTo, setPreviousDateTo] = useState<Date | null>(null)
	const [isDatepickerOpen, setIsDatepickerOpen] = useState(false)
	const [datesAreChanged, setDatesAreChanged] = useState(false)
	const [inputError, setInputError] = useState<string | null>(null)
	const {
		backTabPartner,
		frontTabPartner,
		hasBackTabRedirect,
		hasFrontPartner,
		isPartnerBooking,
		partnerInfo,
	} = getRedirectPartnerInfo(urlSearchParams)
	const shouldIgnoreFrontTab = frontTabPartner === '0'
	// Initialize checkbox checked state - checked by default when partner search is available
	const [isCompareChecked, setIsCompareChecked] = useState(hasBackTabRedirect && partnerInfo ? true : isPartnerBooking)
	const [userEmail, setUserEmail] = useState('')
	const [emailError, setEmailError] = useState<string>('')
	const [isOpenEmailSignup, setIsOpenEmailSignup] = useState(false)
	const datesManuallySetRef = useRef(false)
	// AM-123 (M2): debounce ONLY the search_submission emit (the search/redirect
	// always runs). handleSearch has no in-flight lock and is also invoked from the
	// signup .finally(), so a double-click / signup path double-counted the event.
	const lastSearchEmitRef = useRef<number>(0)
	// AM-123 (M3): synchronous in-flight guard around the price-alert subscription.
	const emailSubscribeInFlightRef = useRef(false)
	// AM-123 (D1): snapshot of rooms/guests taken when the panel opens, so we only
	// emit guests_select on close when the allocation actually changed.
	const guestsSnapshotRef = useRef<string>('')
	// const isFeatureOn = useFeatureIsOn('google_place')
	// const featureOnRef = useRef(false)

	// Compute checkbox text once for consistency
	const checkboxText = partnerInfo ? getComparisonCheckboxText(partnerInfo, isPartnerBooking, t) : null

	// Extract checkbox visibility conditions for better readability
	const shouldShowComparisonCheckbox = hasBackTabRedirect && isMainPage && !isHotelLandingPage && partnerInfo && checkboxText
	const shouldHideComparisonCheckbox = !shouldShowComparisonCheckbox

	useEffect(() => {
		const handleInputFocus = (inputType: AvailableInputTypes) => {
			setOpenedInput(inputType)

			if (inputType === INPUT.LOCATION && bookingInputDestRef.current) {
				setTimeout(() => bookingInputDestRef.current?.focus(), 0)
			} else if (inputType === INPUT.CALENDAR_FROM) {
				setTimeout(() => {
					openCalendar(INPUT.CALENDAR_FROM)
				}, 0)
			} else if (inputType === INPUT.GUESTS_SELECT && roomsInputRef.current) {
				setTimeout(() => roomsInputRef.current?.focus(), 0)
			}
		}

		const handleFieldError = () => {
			setTimeout(() => {
				setInputError(t('locationError'))
			}, 0)
		}

		const subscriptions = [
			eventBus.subscribe(PS_EVENTS.INPUT_FOCUS, handleInputFocus),
			eventBus.subscribe(PS_EVENTS.FIELD_ERROR, handleFieldError),
			eventBus.subscribe(PS_EVENTS.HANDLE_SEARCH, handleSearch),
		]

		return () => {
			subscriptions.forEach((unsub) => unsub())
		}
	}, [handleSearch])

	// useEffect(() => {
	// 	if (isFeatureOn) {
	// 		featureOnRef.current = true
	// 	}
	// }, [isFeatureOn])

	useEffect(() => {
		if (displayValue) {
			setSearchValue(displayValue)
		}
	}, [displayValue])

	// Update checkbox checked state when URL params change - keep checked by default for partner search
	useEffect(() => {
		setIsCompareChecked(hasBackTabRedirect && partnerInfo ? true : isPartnerBooking)
	}, [hasBackTabRedirect, partnerInfo, isPartnerBooking])

	useEffect(() => {
		if (defaultLocation) {
			setSelectedItem(defaultLocation)

			if (!selectedItemRef.current) {
				selectedItemRef.current = defaultLocation
			}
		}
	}, [defaultLocation])

	useEffect(() => {
		const checkInParam = searchParams.get('check-in-date')
		const checkOutParam = searchParams.get('check-out-date')
		const hadDateParams = !!(checkInParam || checkOutParam)

		const isCheckInValid = !!(checkInParam && checkDate(checkInParam))
		const isCheckOutValid = !!(checkOutParam && checkDate(checkOutParam))

		let needFallback = !isCheckInValid || !isCheckOutValid

		if (!needFallback && checkInParam && checkOutParam) {
			try {
				const inDate = parseStayDateFromUrlOrLegacy(checkInParam)
				const outDate = parseStayDateFromUrlOrLegacy(checkOutParam)
				if (isValid(inDate) && isValid(outDate) && inDate < outDate) {
					setDateFrom(inDate)
					setDateTo(outDate)
					setPreviousDateFrom(inDate)
					setPreviousDateTo(outDate)
				} else {
					needFallback = true
				}
			} catch {
				needFallback = true
			}
		}

		if (needFallback && !datesManuallySetRef.current) {
			const isHotel =
				selectedItem?.loctype === SearchItemLocationType.HOTEL ||
				!!isHotelLandingPage

			const fallbackInDate = addWeeks(new Date(), 1)
			let fallbackOutDate = addWeeks(new Date(), 2)

			if (isHotel) {
				fallbackOutDate = addDays(fallbackInDate, 2)
			}

			setDateFrom(fallbackInDate)
			setDateTo(fallbackOutDate)
			setPreviousDateFrom(fallbackInDate)
			setPreviousDateTo(fallbackOutDate)

			const formattedIn = format(fallbackInDate, DATE_FORMAT_QUERY)
			const formattedOut = format(fallbackOutDate, DATE_FORMAT_QUERY)

			if (hadDateParams && (checkInParam !== formattedIn || checkOutParam !== formattedOut)) {
				updateURL(formattedIn, formattedOut)
			}
		}
	}, [searchParams, selectedItem?.loctype, isMainPage, isHotelLandingPage])

	const updateURL = useCallback(
		(newCheckIn: string, newCheckOut: string) => {
			const current = new URLSearchParams(Array.from(searchParams.entries()))
			current.set('check-in-date', newCheckIn)
			current.set('check-out-date', newCheckOut)

			const search = current.toString()
			const query = search ? `?${search}` : ''
			const path = typeof window !== 'undefined' ? window.location.pathname : ''
			router.replace(`${path}${query}`, { scroll: false })
		},
		[router, searchParams]
	)

	const calendarOpenedRef = useRef(false)

	useEffect(() => {
		//cal=1 logic should be ignored on single hotel page
		if (isHotelPage) return

		// Only attempt to open the calendar once when the component mounts
		if (shouldPreopenCalendar && !calendarOpenedRef.current) {
			calendarOpenedRef.current = true

			if (isMainPage) {
				openCalendar(INPUT.CALENDAR_FROM)
			} else {
				if (!isExpanded) {
					const inputType = isDesktop ? INPUT.CALENDAR_FROM : INPUT.MAIN_FORM
					eventBus.publish(PS_EVENTS.INPUT_FOCUS, inputType)
				} else if (isDesktop) {
					setTimeout(() => {
						openCalendar(INPUT.CALENDAR_FROM)
					}, 300)
				}
			}
		}
	}, [shouldPreopenCalendar, isMainPage, isExpanded, isHotelPage])

	useEffect(() => {
		eventBus.publish(PS_EVENTS.SELECTED_ITEM_CHANGED, {
			hasSelectedItem: !!selectedItem,
			selectedItem: selectedItem,
		})
		if (selectedItem) {
			let formData
			const pt = searchParams.get('pt') || null

			if (
				selectedItem.loctype === SearchItemLocationType.HOTEL ||
				(isHotelPage && hasBackTabRedirect && isPartnerBooking)
			) {
				const buid = searchParams.get('buid') || null

				formData = getFormData({
					...selectedItem,
					dateFrom,
					dateTo,
					roomsAndGuests,
					backTabPartner,
					buid,
					pt,
					formType: FORM_TYPE.BACK,
					displayname: `${selectedItem.name}, ${selectedItem.cityonly}, ${selectedItem.country}`,
				})
			} else {
				const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')
				const localDestcode = destCode?.includes(':')
					? destCode.substring(destCode.indexOf(':') + 1)
					: destCode

				formData = getFormData({
					...selectedItem,
					kayakType: 'city',
					...(selectedItem.ctid && { ptid: `${selectedItem.ctid}` }),
					displayname: selectedItem.cityname,
					dateFrom,
					dateTo,
					roomsAndGuests,
					formType: FORM_TYPE.FRONT,
					locationname: selectedItem.name,
					pt,
					...(hasCityDestcode && { ptid: localDestcode }),
				})
			}

			setFrontFormData(formData)
		}
	}, [selectedItem, dateFrom, dateTo, roomsAndGuests])

	useEffect(() => {
		if (searchParams.has('room')) {
			let roomParam: string[] = searchParams.getAll('room')

			if (!Array.isArray(roomParam)) {
				roomParam = [roomParam]
			}

			const rooms = roomParam.map((room: string) => {
				const [adults, children = ''] = room.split(ROOM_GUESTS_SEPARATOR)
				const childAges = children
					? children.split(',').map((age) => Number(age))
					: []

				return {
					adults: parseInt(adults) || 1, // Default to 1 adult if parsing fails
					childAge: childAges,
				}
			})
			handleRoomsAndGuestsChange(rooms)
		}
	}, [searchParams])

	const fetchSuggestions = useCallback(
		async (query: string) => {
			try {
				const result = await fetchAutocompleteResults(
					query,
					urlParams.lang as string
				)
				setSuggestionsResult(result)

				const suggestions = result.map((item) => ({
					id: item.id,
					title: item.loctype === SearchItemLocationType.HOTEL ? item.hotelname : item.displayname,
					type: item.loctype,
					image: item.destination_images?.image_webp,
					locationName: item.locationname,
					displayName: item.displayname,
					placeID: item.placeID,
					country: item.country,
					cityonly: item.cityonly,
				}))

				setSuggestions(suggestions)
			} catch (error) {
				console.error('Error fetching suggestions:', error)
				setSuggestions([])
			}
		},
		[urlParams.lang]
	)

	const onSuggestionsFetchRequested = (
		params: SuggestionsFetchRequestedParams
	) => {
		fetchSuggestions(params.value)
	}

	const onDateChange = (dates: Date[]) => {
		if (dates[0]?.getTime() === dates[1]?.getTime()) {
			dates[1] = addDays(dates[1], 1)
		}

		datesManuallySetRef.current = true

		if (dates[0]) {
			setDateFrom(dates[0])
			setOpenedInput(INPUT.CALENDAR_TO)
			if (dates[0] && !dates[1]) {
				emitFromDateGAEvent()
			}
		}

		setDateTo(dates[1] ? dates[1] : null)

		if (dates[1]) {
			if (isDesktop) {
				handleClose()
				setIsDatepickerOpen(false)
				setDatesAreChanged(true)
			}
			emitToDateGAEvent()
		}
	}

	const openCalendar = (input: INPUT) => {
		setTimeout(() => {
			handleOpenInput(input)
		}, 100)

		setIsDatepickerOpen(true)
	}

	const handleCancelDateSelection = () => {
		setDateFrom(previousDateFrom)
		setDateTo(previousDateTo)
		setIsDatepickerOpen(false)
		handleClose()
	}

	const onSuggestionSelected = async (
		_event: FormEvent<any>,
		value: SuggestionSelectedEventData<Suggestion>
	) => {
		setInputError(null)
		const { id, placeID } = value.suggestion

		if (id) {
			const selected = find(suggestionsResult, { id: id }) || null
			setSelectedItem(selected)
		} else {
			try {
				const mappedPlace = await parseGooglePlaceToSelectedItem(placeID)
				setSelectedItem(mappedPlace)
				setSelectedSuggestion(mappedPlace.displayname)
			} catch (error) {
				setInputError(t('locationError'))
				return
			}
		}

		setSelectedSuggestion(value.suggestionValue)
		handleClose()

		if (!datesAreChanged) {
			setTimeout(() => {
				openCalendar(INPUT.CALENDAR_FROM)
			}, 200)
		}
	}

	const onInputChange = (value: string) => {
		setInputError(null)
		if (value !== selectedSuggestion) {
			// Removing the suggestion when input changes
			setSelectedItem(null)
		}
	}

	const handleRoomsAndGuestsChange = (rooms: Allocation[]) => {
		const roomsCount = rooms.length
		const guestsCount = getGuestsCount(rooms)

		setRoomsAndGuests(rooms)
		setRoomsAndGuestsText(t('roomsAndGuests', { roomsCount, guestsCount }))

		// handleClose()
	}

	const handleFreeRegion = (selectedItem: SearchItem, dest: string) => {
		const { name, smartyDisplay } = selectedItem

		if (selectedItem.country) {
			return `/${kebabCase(selectedItem.country)}/${kebabCase(name)}/`
		}
		return `${dest}/${kebabCase(smartyDisplay)}/${kebabCase(
			smartyDisplay
		)}/freereg/`
	}

	function handleSearch() {
		// smoothScrollTo(0, 0)
		if (!selectedItem) {
			handleLocationError()
			return
		}

		const frontTabRedirectCriteria =
			(isMainPage || isHotelLandingPage || isHotelPage) && hasFrontPartner && !shouldIgnoreFrontTab

		const current = new URLSearchParams(Array.from(searchParams.entries()))
		const existingParams = parseSearchParamsToLower(
			Object.fromEntries(current.entries())
		)

		const allowedPartners = searchParams.get('ap') || null
		const buid = searchParams.get('buid') || null
		const pt = searchParams.get('pt') || null
		const currency =
			searchParams.get('currency') ||
			userData?.selectedCurrency?.abbreviation ||
			userData?.defaultCurrency?.abbreviation ||
			DEFAULT_CURRENCY
		const userCountry = userData?.userCountry

		const {
			country,
			region,
			loctype,
			cityonly,
			name,
			smartyDisplay,
			kayakId: hotelID,
			placeID,
			airportname,
			timezone,
			ctid,
			cc: countryCode,
			googlePlaceId,
			lat,
			lng,
			utcOffsetMinutes,
		} = selectedItem

		const buildDestination = () => {
			let basePath = urlParams.lang ? `/${urlParams.lang}` : ''

			if (isHotelPage && selectedItem.id === selectedItemRef.current?.id) {
				basePath += '/hotel'
			}

			// TODO: Refine. Is lm actually used?
			switch (loctype) {
				case SearchItemLocationType.AIRPORT:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || ''
					)}/ap/${encodeURIComponent(airportname || name)}`
				case SearchItemLocationType.HOTEL:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}/${encodeURIComponent(name)}`
				case SearchItemLocationType.FREEREGION:
					return handleFreeRegion(selectedItem, basePath)
				case SearchItemLocationType.CITY:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}`
				case SearchItemLocationType.LM:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}/lm/${encodeURIComponent(name)}`
				// return `${basePath}/${kebabCase(country)}${
				// 	region ? `/${kebabCase(region)}` : ''
				// }${cityonly ? `/${kebabCase(cityonly)}` : ''}`
				case SearchItemLocationType.HOOD:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}/hood/${encodeURIComponent(name)}`
				default:
					return `${basePath}/${kebabCase(country)}${region ? `/${kebabCase(region)}` : ''
						}`
			}
		}

		const dest = buildDestination()

		// Validate dates before formatting
		if (!validateDateRange(dateFrom, dateTo) || !dateFrom || !dateTo) {
			console.error('Date validation failed')
			Sentry.captureMessage('Date validation failed in handleSearch', {
				level: 'error',
				tags: {
					component: 'Booking Form',
					action: 'handleSearch',
					issue: 'date_validation_failed',
				},
				extra: {
					dateFrom: dateFrom,
					dateTo: dateTo || 'Not Selected',
					maxAllowedDate: addMonths(new Date(), 12),
					selectedItem: selectedItem
						? {
							id: selectedItem.id,
							name: selectedItem.name,
							loctype: selectedItem.loctype,
						}
						: null,
					userAgent: navigator.userAgent,
				},
			})
			return
		}

		const formattedDateFrom = format(dateFrom, DATE_FORMAT_QUERY)
		const formattedDateTo = format(dateTo, DATE_FORMAT_QUERY)

		const rooms = roomsAndGuests.map(
			(room) =>
				`${room.adults}|${room.childAge.map((age) => age.toString()).join(',')}`
		)

		const {
			loctype: paramLoctype,
			entitykey: paramEntityKey,
			slug,
			destcode: paramDestCode,
			locationname,
			placeid: paramPlaceID,
			hotelid: paramHotelID,
			usercountry: paramUserCountry,
			sessionid: paramSessionID,
			timezone: paramTimezone,
			f: frontParam,
			latlon: paramLatLon,
			utcoffsetminutes: paramUtcOffsetMinutes,
			googleplaceid: paramGooglePlaceID,
			page,
			fuid,
			mx: maxPriceParam,
			mn: minPriceParam,
			cal: openCalendarParam,
			...restParams
		} = existingParams || {}

		// This is to remove filters from the results page
		const filteredParams = isMainPage
			? restParams
			: Object.fromEntries(
				Object.entries(restParams).filter(
					([key]) =>
						!['s', 'h', 't', 'gr', 'by', 'dir', 'p', 'fa'].includes(key)
				)
			)

		const urlParamsObject = new URLSearchParams()
		urlParamsObject.set('currency', currency)
		urlParamsObject.set('check-in-date', formattedDateFrom)
		urlParamsObject.set('check-out-date', formattedDateTo)
		urlParamsObject.set('userCountry', userCountry)
		urlParamsObject.set('lat', String(lat));
		urlParamsObject.set('lng', String(lng));
		urlParamsObject.set('zoom', selectedItem.loctype === SearchItemLocationType.REGION ? '9' : '14');

		const cityName = selectedItem.cityonly && selectedItem.cityonly

		if (selectedItem.loctype === SearchItemLocationType.AIRPORT) {
			urlParamsObject.set('locationName', cityName || selectedItem.name)
		} else if (selectedItem.loctype === SearchItemLocationType.CITY) {
			urlParamsObject.set('locationName', cityName)
		} else if (selectedItem.loctype === SearchItemLocationType.HOTEL) {
			urlParamsObject.set('locationName', selectedItem.name)
		} else if (
			selectedItem.loctype === SearchItemLocationType.REGION ||
			selectedItem.loctype === SearchItemLocationType.COUNTRY ||
			selectedItem.loctype === SearchItemLocationType.FREEREGION
		) {
			urlParamsObject.set(
				'locationName',
				startCase(camelCase(selectedItem.name))
			)
		}

		// if (customEntityKey) {
		// 	urlParamsObject.set('entityKey', customEntityKey)
		// }
		if (userData?.sessionID) {
			urlParamsObject.set('sessionID', userData?.sessionID)
		}
		if (timezone) {
			urlParamsObject.set('timezone', timezone)
		}
		if (ctid) {
			urlParamsObject.set('ctid', ctid)
		}
		if (countryCode) {
			urlParamsObject.set('cc', countryCode)
		}
		if (placeID) {
			urlParamsObject.set('placeID', placeID)
		}

		if (
			selectedItem.loctype === SearchItemLocationType.HOTEL &&
			selectedItem.kayakId
		) {
			urlParamsObject.set('hotelID', hotelID)
		}
		if (allowedPartners) {
			urlParamsObject.set('ap', allowedPartners)
		}
		if (googlePlaceId) {
			urlParamsObject.set('googlePlaceId', googlePlaceId)
			urlParamsObject.set('latlon', `${lat},${lng}`)
			urlParamsObject.set('utcOffsetMinutes', String(utcOffsetMinutes))
		}

		rooms.forEach((room) => {
			urlParamsObject.append('room', room)
		})

		Object.entries(filteredParams).forEach(([key, value]) => {
			if (!urlParamsObject.has(key)) {
				urlParamsObject.set(key, value)
			}
		})

		// Add b parameter when compare with checkbox is checked and partner info exists
		if (isCompareChecked && backTabPartner && partnerInfo) {
			urlParamsObject.set('b', backTabPartner)
		}

		const url = `${dest}?${urlParamsObject.toString()}`

		setShowCookieNotice(false)

		// AM-123 (M2): debounce only the analytics emit; the search still proceeds.
		const nowTs = Date.now()
		if (nowTs - lastSearchEmitRef.current > 1000) {
			lastSearchEmitRef.current = nowTs
			emitFormSearchGAEvent({
				dateFrom: formattedDateFrom,
				dateTo: formattedDateTo,
				roomsAndGuests,
				value: smartyDisplay,
			})
		}

		const backTabRedirectCriteria =
			hasBackTabRedirect &&
			(
				isHotelLandingPage ||
				isHotelPage ||
				(isMainPage && ((isCompareChecked && partnerInfo) || urlSearchParams.has('b')))
			)

		const hasExplicitBackAndFrontPartners =
			urlSearchParams.has('b') && urlSearchParams.has('f') && !shouldIgnoreFrontTab

		const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')
		const localDestcode = destCode?.includes(':')
			? destCode.substring(destCode.indexOf(':') + 1)
			: destCode

		const getCommonFormData = () => ({
			...selectedItem,
			dateFrom,
			dateTo,
			roomsAndGuests,
			backTabPartner,
			frontTabPartner,
			pt,
			buid,
			...(hasCityDestcode && { ptid: localDestcode }),
		})

		const handleRedirect = (
			formType: FORM_TYPE,
			shouldOpenResults: boolean = false
		) => {
			// Using fresh search params from window to avoid any React state sync issues
			const freshParams = new URLSearchParams(window.location.search)
			const freshF = freshParams.get('f')?.toLowerCase()
			const freshFuid = freshParams.get('fuid')
			const freshB = freshParams.get('b')?.toLowerCase()
			const freshBuid = freshParams.get('buid')

			let finalFrontPartner: string | null | undefined = frontTabPartner
			let finalBackPartner: string | null | undefined = backTabPartner
			let finalFuid: string | null | undefined = fuid
			let finalBuid: string | null | undefined = buid

			if (formType === FORM_TYPE.FRONT) {
				const { f: normF, fuid: normFuid } = normalizeFrontPartnerParams(
					freshF || frontTabPartner || undefined,
					freshFuid || fuid || undefined
				)
				finalFrontPartner = normF
				finalFuid = normFuid

				// Pass through back params as is for the link, but don't let them affect front logic
				finalBackPartner = freshB || backTabPartner
				finalBuid = freshBuid || buid
			} else {
				// FORM_TYPE.BACK
				const { b: normB, buid: normBuid } = normalizeBackPartnerParams(
					freshB || backTabPartner || undefined,
					freshBuid || buid || undefined
				)
				finalBackPartner = normB
				finalBuid = normBuid

				// Preserve front params
				finalFrontPartner = freshF || frontTabPartner
				finalFuid = freshFuid || fuid
			}

			const formData = getFormData({
				...getCommonFormData(),
				frontTabPartner: finalFrontPartner,
				backTabPartner: finalBackPartner,
				fuid: finalFuid,
				buid: finalBuid,
				formType,
			})

			if (formType === FORM_TYPE.BACK) {
				if (shouldOpenResults) {
					window.open(url, '_blank', 'noopener,noreferrer')
				}
				submitHiddenForm(formData, redirectFormRef.current as HTMLFormElement)
			} else if (formType === FORM_TYPE.FRONT) {
				// Use the normalized finalFrontPartner for logic
				const isKayakFlow = finalFrontPartner === 'km' && !!finalFuid
				const route = isKayakFlow ? 'redirect' : 'passthru'
				const redirectUrl = `/${urlParams.lang}/${route}?${serialize(formData)}`

				// For both Kayak and non-Kayak flows with FORM_TYPE.FRONT: affiliate in new tab, current tab to results (like f=b behavior)
				window.open(redirectUrl, '_blank', 'noopener,noreferrer')
				if (shouldOpenResults) {
					router.push(url)
				}
				setFrontFormData(formData)
			}
		}

		// Determine if exactly one criteria is met
		// If both b and f are explicitly provided, do not navigate to results here.
		const shouldOpenResultsPage = hasExplicitBackAndFrontPartners
			? false
			: frontTabRedirectCriteria !== backTabRedirectCriteria

		if (frontTabRedirectCriteria) {
			handleRedirect(FORM_TYPE.FRONT, shouldOpenResultsPage)
		}

		if (backTabRedirectCriteria) {
			const openFrontTabResults =
				!hasExplicitBackAndFrontPartners && shouldOpenResultsPage && !shouldIgnoreFrontTab
			handleRedirect(FORM_TYPE.BACK, openFrontTabResults)
		}

		// Only redirect to results page if neither criteria is met
		if (!backTabRedirectCriteria && !frontTabRedirectCriteria) {
			router.push(url)
		}
	}

	const handleUserEmailChange = (value: string) => {
		setUserEmail(value)
		setEmailError('')
	}
	const handleLocationError = () => {
		onInputFocus()
		if (!isMobile) {
			setTimeout(() => {
				setInputError(t('locationError'))
			}, 100)
		}

		emitSearchWithoutLocationGAEvent()
	}

	const handleSignupAndSearch = (email: string) => {
		if (!selectedItem) {
			handleLocationError()
			return
		}

		if (email.length) {
			const validEmail = validateEmail(email)

			if (!validEmail) {
				setEmailError(t('emailError'))
				return
			}
		}

		if (!email) {
			emitFormEmailSubscribeBounceGAEvent()
			setIsOpenEmailSignup(false)
			handleSearch()
			return
		}

		const buid = searchParams.get('buid') || null
		const fuid = searchParams.get('fuid') || null
		const pt = searchParams.get('pt') || null
		const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')

		const formData = getFormData({
			...selectedItem,
			dateFrom,
			dateTo,
			roomsAndGuests,
			buid,
			fuid,
			pt,
			...(hasCityDestcode && { ptid: destCode }),
		})

		const {
			_adults,
			_children,
			...restFormData
		} = formData
		const { placeID, kayakId: hotelID, kayakType } = selectedItem
		const isHotel = selectedItem.loctype === SearchItemLocationType.HOTEL
		const currency =
			searchParams.get('currency') ||
			userData?.selectedCurrency?.abbreviation ||
			userData.defaultCurrency.abbreviation

		// AM-123 (M3): guard against double-submit so the subscription (and its
		// conversion events) fire once per click-burst.
		if (emailSubscribeInFlightRef.current) return
		emailSubscribeInFlightRef.current = true

		priceAlertSubscription({
			...restFormData,
			email: email,
			adults: formData._adults,
			children: formData._children,
			lng: urlParams.lang as string,
			currency: currency,
			'destination-id': placeID && placeID,
		})
			.then(() => {
				console.log('subscription successful')
				emitFormEmailSubmitGAEvent()
				// AM-123 (D3): sign_up_deals was imported but never called, so the
				// deals-signup conversion never fired. Emit it on subscription success.
				// NOTE for analytics: confirm whether sign_up_deals or
				// form_email_subscribe is the canonical conversion (they now both fire here).
				emitSignUpDealsGAEvent()
			})
			.catch((error) => {
				console.error('An error occurred during subscription:', error)
			})
			.finally(() => {
				emailSubscribeInFlightRef.current = false
				setUserData({ ...userData, email })
				handleSearch()
			})
	}

	const handleOpenEmailDialog = () => {
		if (authData?.user?.email) {
			handleSignupAndSearch(authData?.user?.email)
			return
		}

		if (!selectedItem) {
			if (isMobile) {
			} else {
				onInputFocus()
				setInputError(t('locationError'))
			}

			return
		}
		setIsOpenEmailSignup(true)
	}

	const onCalendarClose = () => {
		if (dateFrom && dateTo === null) {
			const suggestedCheckout = addDays(dateFrom, 1)
			const maxDate = addMonths(new Date(), 12)

			if (suggestedCheckout <= maxDate) {
				setDateTo(suggestedCheckout)
			}
		}

		setIsDatepickerOpen(false)
		handleClose()
	}

	const onRoomsAndGuestsOpen = () => {
		// AM-123 (D1): snapshot the allocation on open so close can tell if it changed.
		guestsSnapshotRef.current = JSON.stringify(roomsAndGuests)
		handleOpenInput(INPUT.GUESTS_SELECT)
		emitGuestsClickGAEvent()
	}

	const onRoomsAndGuestsClose = () => {
		if (!isDesktop) {
			smoothScrollTo(0, 0)
		}

		handleClose()

		// AM-123 (D1): only emit guests_select when the allocation actually changed
		// (it previously fired on every panel close, even with no change).
		if (JSON.stringify(roomsAndGuests) === guestsSnapshotRef.current) {
			return
		}

		emitGuestsSelectGAEvent({
			rooms: roomsAndGuests.length,
			adults: roomsAndGuests.reduce((acc, room) => acc + room.adults, 0),
			children: roomsAndGuests.reduce(
				(acc, room) => acc + room.childAge.length,
				0
			),
			total: getGuestsCount(roomsAndGuests),
		})
	}

	const handleApplyGuestsAndSearch = () => {
		handleOpenInput(null)
		handleSearch()
	}

	useEffect(() => {
		if (isMobile && isDatepickerOpen) {
			setTimeout(() => scrollToSelectedMonth(dateFrom as Date), 0)
		}
	}, [isDatepickerOpen])

	const handleApplyDates = () => {
		setIsDatepickerOpen(false)
		setPreviousDateFrom(dateFrom)
		setPreviousDateTo(dateTo)
		setDatesAreChanged(true)
		eventBus.publish(PS_EVENTS.INPUT_FOCUS, INPUT.MAIN_FORM)
	}

	const handleOpenInput = (input: AvailableInputTypes) => {
		if (openedInput === INPUT.LOCATION && input === null) {
			smoothScrollTo(0, 0)
		}

		setInputError(null)
		setOpenedInput(input)

		if (smHeight && isDesktop) {
			bookingRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			})
		}
	}

	const handleClose = () => {
		setOpenedInput(null)
		if (!isDesktop) {
			eventBus.publish(PS_EVENTS.INPUT_FOCUS, INPUT.MAIN_FORM)
		}
		document.body.style.overflowY = 'initial'
	}

	const onInputFocus = (input: INPUT = INPUT.LOCATION) => {
		const isLocationInput = input === INPUT.LOCATION
		handleOpenInput(input)

		if (isLocationInput) {
			bookingInputDestRef.current?.focus()
		}

		if (!isDesktop) {
			document.body.style.overflowY = 'hidden'
		}
	}

	return (
		<>
			{isMainPage && mobileHeadingSlot && (
				<div className="lg:hidden text-center mb-3">
					{mobileHeadingSlot}
				</div>
			)}
			<div
				className={classNames('booking__form', {
					'max-lg:pt-3 max-lg:border border-text-300 md:max-lg:mb-3 md:max-lg:mx-3':
						isMainPage,
				})}
			>
				{/* Anchor (autoscroll) */}
				<div className="relative" style={{ top: '-110px' }} ref={bookingRef} />
				<div
					ref={bookingInputsRef}
					className={classNames(
						'booking w-full flex lg:mt-8 justify-center flex-wrap mx-auto',
						{ 'max-lg:px-3 xl:max-w-[1048px] lg:max-xl:container': isMainPage },
						{ 'max-w-[1048px]': !isMainPage }
					)}
				>
					<form
						ref={redirectFormRef}
						id="redirect-form"
						method="post"
						action={process.env.NEXT_PUBLIC_BACKEND_URL + '/hotels/redirect'}
					/>
					<form
						ref={redirectFormBlankRef}
						id="redirect-form_blank"
						method="post"
						target="_blank"
						action={process.env.NEXT_PUBLIC_BACKEND_URL + '/hotels/redirect'}
					/>
					<div className="booking__wrapper">
						<div
							className={classNames(
								'flex-1 w-full lg:h-full md:max-lg:mb-2 max-lg:mb-3',
								{
									'lg:my-0': isMainPage,
								}
							)}
						>
							<SearchAutosuggest
								ref={bookingInputDestRef}
								defaultValue={capitalize(
									searchValue?.displayValue || searchValue?.displayName || ''
								)}
								suggestions={suggestions}
								inputError={inputError}
								onSuggestionsFetchRequested={onSuggestionsFetchRequested}
								onSuggestionSelected={onSuggestionSelected}
								onInputChange={onInputChange}
								onInputFocus={onInputFocus}
								onInputBlur={handleClose}
								inputClassName={classNames('search-autosuggest__input', {
									'search-autosuggest__input--is-open new-autosuggest-input--open':
										openedInput === INPUT.LOCATION,
								})}
								lang={urlParams.lang as string}
								pageType={
									isMainPage
										? PAGE_TYPE.MAIN_PAGE
										: isHotelPage
											? PAGE_TYPE.HOTEL_PAGE
											: PAGE_TYPE.RESULTS_PAGE
								}
							/>
						</div>
						<BookingDatepicker
							dateFrom={dateFrom}
							dateTo={dateTo}
							isDatepickerOpen={isDatepickerOpen}
							openedInput={openedInput}
							isMobile={!isDesktop}
							isDesktop={isDesktop}
							locale={locale}
							onDateChange={onDateChange}
							onCalendarClose={onCalendarClose}
							openCalendar={openCalendar}
							handleCancelDateSelection={handleCancelDateSelection}
							handleApplyDates={handleApplyDates}
						/>

						<div ref={roomsInputRef} className="relative -top-2"></div>
						<div
							className={classNames(
								'booking__guests-input booking__additional-input',
								{
									'guests-open !border-text-700':
										openedInput === INPUT.GUESTS_SELECT,
								}
							)}
							id={INPUT.GUESTS_SELECT}
							data-test-id="GUESTS_SELECT"
						>
							<FormInput
								id={'rooms-and-guests-input'}
								onClick={onRoomsAndGuestsOpen}
								value={roomsAndGuestsText}
								className={classNames('outline-none max-lg:h-14 rounded-full', {
									'rooms-and-guests-input--is-open':
										openedInput === INPUT.GUESTS_SELECT,
								})}
								readonly
								ref={bookingInputGuestsRef}
							>
								<Icons.iconGuests />
							</FormInput>
							{!isDesktop ? (
								<RemoveScroll
									enabled={isMobile && openedInput === INPUT.GUESTS_SELECT}
									removeScrollBar={false}
								>
									<FullScreenDrawer
										isOpen={openedInput === INPUT.GUESTS_SELECT}
										onClose={onRoomsAndGuestsClose}
										headerText={t('roomsAndGuestsLabel')}
									>
										<GuestsSelect
											onApplyGuestsAndSearch={handleApplyGuestsAndSearch}
											onChange={handleRoomsAndGuestsChange}
											initialValue={roomsAndGuests}
											onClose={onRoomsAndGuestsClose}
											isMobile={true}
											isMainPage={isMainPage as boolean}
										/>
									</FullScreenDrawer>
								</RemoveScroll>
							) : (
								<Popup
									className="rounded-lg right-0 top-0 lg:top-[68px] z-[70]"
									isOpen={openedInput === INPUT.GUESTS_SELECT}
									onClose={onRoomsAndGuestsClose}
								>
									<GuestsSelect
										onApplyGuestsAndSearch={handleApplyGuestsAndSearch}
										onChange={handleRoomsAndGuestsChange}
										initialValue={roomsAndGuests}
										onClose={onRoomsAndGuestsClose}
										isMobile={false}
										isMainPage={isMainPage as boolean}
									/>
								</Popup>
							)}
						</div>
						<div
							data-test="search-button"
							className="hidden lg:block lg:order-2 xl:order-none"
						>
							<Button
								id="search-button"
								bold
								textColor="white"
								onClick={authData?.user?.email ? handleOpenEmailDialog : handleSearch}
								name="Search"
								className={classNames(
									'!bg-button-main text-xl h-14 hover:opacity-80 !rounded-full transition-all ease-linear w-fit flex items-center justify-center'
								)}
							>
								<Icons.iconLens className="xl:mr-3" />
								<span className="hidden xl:inline">
									{t('newDesign.searchButton')}
								</span>
							</Button>
						</div>
					</div>

					{/* Never here for now */}
					{!isDesktop && isMainPage && (
						<>
							{shouldShowComparisonCheckbox && (
								<div className="price-alert-switch-mobile my-6 lg:mt-0 w-full flex justify-start max-lg:mb-0">
									<Checkbox
										labelLeft={checkboxText.leftLabel}
										labelRight={checkboxText.rightLabel}
										checked={isCompareChecked}
										onChange={setIsCompareChecked}
										testId="compare-with-booking"
										variant="main-dark"
										bold
									/>
								</div>
							)}
						</>
					)}
					<div
						data-test="search-button"
						className="block lg:hidden w-full max-lg:mt-6 lg:px-2 lg:order-3 xl:order-none"
					>
						<Button
							id="search-button"
							big
							bgColor="main"
							textColor="white"
							onClick={
								authData?.user?.email
									? () => handleSignupAndSearch(authData?.user?.email! || userEmail)
									: handleSearch
							}
							name="Search"
							className={classNames(
								'font-bold text-xl !bg-button-main h-14 !rounded-full transition-all ease-linear flex items-center justify-center',
								{
									'mt-3': !isMainPage,
								}
							)}
						>
							<Icons.iconLens className="mr-3" />

							{isMobile && !isDesktop
								? t('newDesign.searchButtonMobile')
								: t('newDesign.searchButton')}
						</Button>
					</div>
					<div
						className={classNames(
							'hidden lg:order-2 xl:order-none w-full xl:max-w-[1040px]',
							{
								hidden: !isMainPage,
								'lg:flex ': isMainPage,
							}
						)}
					>
						<div
							className={classNames(
								' flex items-center justify-between mt-4 w-full ps-2 lg:ps-5 pe-2 lg:pe-5 mb-4 lg:mb-0',
								{
									'lg:!justify-start': shouldHideComparisonCheckbox,
								}
							)}
						>
							<div className={classNames(
								"price-alert-switch",
								{
									'invisible pointer-events-none': shouldHideComparisonCheckbox
								}
							)}>
								{checkboxText && (
									<Checkbox
										labelLeft={checkboxText.leftLabel}
										labelRight={checkboxText.rightLabel}
										checked={isCompareChecked}
										onChange={setIsCompareChecked}
										testId="compare-with-booking"
										variant="white"
										bold
									/>
								)}
							</div>
						</div>
					</div>
				</div>
				{isOpenEmailSignup && selectedItem && isDesktop && (
					<EmailSignup
						destination={selectedItem as SearchItem}
						startDate={dateFrom as Date}
						endDate={dateTo as Date}
						isOpen={true}
						onSubmit={handleSignupAndSearch}
						guestCount={getGuestsCount(roomsAndGuests)}
					/>
				)}
			</div>
		</>
	)
}

export default dynamic(() => Promise.resolve(Booking), {
	ssr: false,
	suspense: true,
	loading: () => (
		<>
			<div className="home-page-placeholder">
				<BookingPlaceholder />
			</div>
			<div className="results-page-placeholder">{/* <TopLoading /> */}</div>
		</>
	),
})
